"use client";

import CenterCard from "@/components/feature/influence/CenterCard";
import CreatorSelectModal from "@/components/feature/influence/CreatorSelectModal";
import InfluenceHeader from "@/components/feature/influence/InfluenceHeader";
import InfluenceMap from "@/components/feature/influence/InfluenceMap";
import {
  getCreatorInfluence,
  getCreatorsWithInput,
  type CreatorInfluenceData,
} from "@/lib/influence/getInfluenceData";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { useMemo, useState } from "react";
import FloatingParticles from "~/src/components/feature/influence/FloatingParticles";

// デフォルト表示用のクリエイターID
const DEFAULT_CREATOR_ID = "creator_冨樫義博";

export default function InfluencePage() {
  // 表示データ
  const [creatorData, setCreatorData] = useState<CreatorInfluenceData | null>(() =>
    getCreatorInfluence(DEFAULT_CREATOR_ID),
  );
  const [mapKey, setMapKey] = useState(DEFAULT_CREATOR_ID);

  // モーダル
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const creatorsWithInput = useMemo(() => getCreatorsWithInput(), []);

  const handleSelectCreator = (id: string) => {
    const data = getCreatorInfluence(id);
    if (data) {
      setCreatorData(data);
      setMapKey(id);
    }
    setIsModalOpen(false);
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-[#0a0a0a] to-[#1a1a2e] px-5 py-10 pb-20 text-white">
      <FloatingParticles />

      <Link
        href="/"
        className="fixed left-4 top-4 z-50 flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/70 backdrop-blur-md transition-all duration-300 hover:border-[#A594F9]/50 hover:bg-white/10 hover:text-white hover:shadow-[0_0_15px_rgba(165,148,249,0.3)]"
      >
        <span>←</span>
        <span className="hidden sm:inline">Top</span>
      </Link>

      <button
        type="button"
        onClick={() => setIsAboutOpen(true)}
        className="fixed right-4 top-4 z-50 flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/70 backdrop-blur-md transition-all duration-300 hover:border-[#A594F9]/50 hover:bg-white/10 hover:text-white hover:shadow-[0_0_15px_rgba(165,148,249,0.3)]"
      >
        <span className="hidden sm:inline">About</span>
        <span>?</span>
      </button>

      <Dialog
        open={isAboutOpen}
        onClose={setIsAboutOpen}
        className="relative z-50"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 flex items-center justify-center overflow-y-auto p-4">
          <DialogPanel
            transition
            className="relative w-full max-w-md overflow-hidden rounded-xl bg-WHITE_1/10 shadow-2xl transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:max-w-xl"
          >
            {/* クローズボタン */}
            <button
              type="button"
              onClick={() => setIsAboutOpen(false)}
              className="absolute right-4 top-4 text-white/50 transition-colors hover:text-white"
            >
              <XMarkIcon className="size-6" />
            </button>

            {/* ヘッダー装飾エリア */}
            <div className="flex flex-col items-center gap-3 bg-gradient-to-br from-[#667eea]/20 to-[#A594F9]/60 px-6 py-4">
              <h2 className="bg-gradient-to-br from-white to-[#8b92a8] bg-clip-text text-xl font-bold text-white">
                Influence Map
              </h2>
            </div>

            {/* 本文 */}
            <div className="p-14">
              <p className="text-base/10 text-white/90">
                憧れのクリエイターを理解するには、 ”その人が何に影響を受けたのか”
                を知る必要があります。
                私たちの感性や思考は、過去に見てきたものによって形成されるからです。
                作家のルーツを辿ることは、その作家に近づくための第一歩になります。
                尊敬する人の創造の源流を辿り、その人が「なにをみてきたか」を見ていきましょう。
              </p>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <div className="relative mx-auto max-w-[1600px]">
        <InfluenceHeader
          title="Creator Influence Map"
          subtitle="創造の源泉を辿る"
        />

        {/* 創作者選択ボタン */}
        <div className="mb-10 flex justify-center">
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="group flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.2em] text-[#A594F9] drop-shadow-[0_0_10px_rgba(165,148,249,0.6)] transition-all duration-300"
          >
            <span className="h-px w-8 bg-[#A594F9]/60 transition-all duration-300 group-hover:w-12" />
            View Creators
            <span className="h-px w-8 bg-[#A594F9]/60 transition-all duration-300 group-hover:w-12" />
          </button>
        </div>

        {/* 創作者選択モーダル */}
        <CreatorSelectModal
          creators={creatorsWithInput}
          isOpen={isModalOpen}
          onSelect={handleSelectCreator}
          onClose={() => setIsModalOpen(false)}
        />

        {/* Influence Map表示 */}
        {creatorData ? (
          <InfluenceMap
            key={mapKey}
            centerContent={
              <CenterCard
                type="creator"
                avatar={creatorData.creator.avatar}
                name={creatorData.creator.name}
                role={creatorData.creator.role}
                profile={creatorData.creator.profile}
              />
            }
            outputCreators={creatorData.outputCreators}
            outputWorks={creatorData.outputWorks}
            inputCreators={creatorData.inputCreators}
            inputWorks={creatorData.inputWorks}
            onViewInfluence={handleSelectCreator}
          />
        ) : null}
      </div>
    </div>
  );
}
